import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Table, Row, Col, Button, Drawer } from 'antd';
import type { TableProps } from 'antd';
import { RegimesConfigData, TranslationData } from '../../../../../../models/MappingData';
import { DeleteOutlined, EditOutlined, ImportOutlined } from '@ant-design/icons';
import { deleteRegimesConfigMapping, fetchRegimesConfigMapping } from '../../../../../../api/repositoryEP';
import './RegimesConfig.scss';
import Search from 'antd/es/input/Search';
import RegimesForm from '../Forms/RegimesForm/RegimesForm';
import ModalGeneric from '../Modals/ModalGeneric';
import { PageSize } from '../../../../../../utils/constants';
import { CustomPagination } from '../../../../..';
import { GenericModal } from '../../../../../common/Modal/GenericModal';

interface RegimesConfigProps {
    language: string;
}

const RegimesConfig = (props: RegimesConfigProps): JSX.Element => {
    const { t, i18n } = useTranslation();
    const [data, setData] = useState<RegimesConfigData[]>([]);
    const [loading, setLoading] = useState<boolean>(true);  // Estado para el indicador de carga
    const drawerWidth = window.innerWidth <= 768 ? '100%' : '40%';
    const [newRegimeDrawer, setNewRegimeDrawer] = useState<boolean>(false);
    const [editRegimeDrawer, setEditRegimeDrawer] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [regimenData, setRegimenData] = useState<RegimesConfigData | null>(null);


    const [actualPage, setActualPage] = useState<number>(1);
    const [actualPageSize, setActualPageSize] = useState<number>(PageSize);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [filter, setFilter] = useState<string>('');


    const onChangePagination = (newPage: number, newPageSize?: number | undefined) => {
        setActualPage(newPage);
        setActualPageSize(newPageSize ?? PageSize);
    }

    useEffect(() => {
        if (!newRegimeDrawer && !editRegimeDrawer && !deleteModalVisible) {
            setLoading(true);
            fetchRegimesConfigMapping(filter, props.language, actualPage, actualPageSize).then((response) => {
                if (response?.type === 1) {
                    setData(response.data.elements);
                    setTotalRecords(response?.data.totalRecords as number);
                }
                setLoading(false);
            });
        }
    }, [newRegimeDrawer, editRegimeDrawer, deleteModalVisible, props.language, actualPage, actualPageSize, filter]);


    const columns: TableProps<RegimesConfigData>['columns'] = [
        {
            title: t("components.tab-mapping.regimes-config.table.c1"),
            dataIndex: 'name',
            key: 'name',
            render: (names: TranslationData[]) => {
                const normalizedLanguage = i18n.language.slice(0, 2).toLowerCase();
                const nameInLanguage = names.find((name: TranslationData) => name.language.slice(0, 2).toLowerCase() === normalizedLanguage);
                return nameInLanguage?.value || names[0]?.value || 'No name available';
            },
        },
        {
            title: t("components.tab-mapping.regimes-config.table.c2"),
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: t("components.tab-mapping.regimes-config.table.c3"),
            dataIndex: 'integrationCode',
            key: 'integrationCode',
        },
        {
            title: t("components.tab-mapping.regimes-config.table.c4"),
            dataIndex: 'internNote',
            key: 'interNote',
        },
        {
            title: t("components.tab-mapping.regimes-config.table.c5"),
            key: 'action',
            render: (obj) => (
                <div className='app-icon-table-container'>
                    <EditOutlined className='app-icon-table' onClick={() => editRegimen(obj)} />
                    <DeleteOutlined className='app-icon-table' onClick={() => openDeleteModal(obj)} />
                </div>
            ),
        },
    ];

    const editRegimen = (regimen: RegimesConfigData) => {
        setRegimenData(regimen);
        setEditRegimeDrawer(true);
    };


    const onSearch = (value: string) => {
        setFilter(value);
        setActualPage(1); //  muy importante: empezar siempre en la primera página
    };

    const handleAddButton = () => {
        setRegimenData(null);
        setNewRegimeDrawer(true);
    };

    const [deletingRegimen, setDeletingRegimen] = useState<boolean>(false);

    const openDeleteModal = (regimen: RegimesConfigData) => {
        setRegimenData(regimen);
        setDeleteModalVisible(true);
    }

    const deleteRegimen = async () => {
        if (regimenData?.id) {
            setDeletingRegimen(true);
            deleteRegimesConfigMapping(regimenData.id).then((r) => {
                if (r?.type === 1) {
                    setDeletingRegimen(false);
                    setDeleteModalVisible(false);
                    setRegimenData(null);
                } else {
                    setDeletingRegimen(false);

                }
            });
        }
    };
    const cancelDeleteRegimen = () => {
        setDeleteModalVisible(false);
        setRegimenData(null);
    }


    return (
        <div className="regimen-config-component">

            {deleteModalVisible && regimenData?.id && (
                <GenericModal
                    isLoading={deletingRegimen}
                    title={t("components.tab-mapping.room-config.modal-title")}
                    closable
                    content={<div>{t("components.tab-mapping.room-config.modal-msg")}</div>}
                    visible={deleteModalVisible}
                    onOk={deleteRegimen}
                    onCancel={cancelDeleteRegimen}
                />
            )}

            <Drawer
                open={editRegimeDrawer}
                title={t("components.tab-mapping.regimes-config.drawer.title-edit")}
                onClose={() => {
                    setEditRegimeDrawer(false);
                    setRegimenData(null);
                }}
                width={drawerWidth}
                closable
            >
                <RegimesForm
                    setDrawerVisible={setEditRegimeDrawer}
                    drawerVisible={editRegimeDrawer}
                    regimesData={regimenData}
                    setRegimesData={setRegimenData}
                />
            </Drawer>

            <Drawer
                open={newRegimeDrawer}
                title={t("components.tab-mapping.regimes-config.drawer.title-new")}
                onClose={() => {
                    setNewRegimeDrawer(false);
                    setRegimenData(null);
                }}
                width={drawerWidth}
                closable
            >
                <RegimesForm
                    setDrawerVisible={setNewRegimeDrawer}
                    drawerVisible={newRegimeDrawer}
                    regimesData={null}
                    setRegimesData={setRegimenData}
                />
            </Drawer>

            <Row gutter={[10, 10]} style={{ flexDirection: 'column' }}>
                <Col xs={24} md={24}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={24} style={{ fontFamily: 'Montserrat' }}>
                            {t("components.tab-mapping.regimes-config.msg-info")}
                        </Col>
                        <Col xs={24} md={24} lg={16}>
                            <Search
                                className="app-select"
                                placeholder={t("components.tab-mapping.regimes-config.search") || ''}
                                allowClear
                                onSearch={onSearch}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={12} lg={4}>
                            <Button className='app-button' onClick={handleAddButton}>
                                {t("components.tab-mapping.regimes-config.button-add")}
                            </Button>
                        </Col>
                        <Col xs={24} sm={12} md={12} lg={4}>
                            <Button disabled className='app-button'><ImportOutlined /> {t("components.tab-mapping.room-config.button-import")}</Button>
                        </Col>
                    </Row>
                </Col>

                <Col xs={24} md={24}>
                    <Table
                        className='table'
                        columns={columns}
                        dataSource={data}
                        loading={loading}  // Indicador de carga
                        scroll={{ x: 'max-content' }}
                        pagination={false}

                    />
                </Col>
            </Row>
            {(
                totalRecords > 0 && (
                    <div style={{ marginTop: '15px' }}>
                        <CustomPagination
                            actualPage={actualPage}
                            actualPageSize={actualPageSize}
                            totalElements={totalRecords}
                            onChange={onChangePagination}
                        />
                    </div>
                )
            )}
        </div>
    );
};

export default RegimesConfig;
